import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/shared/components/ui/card";
import { Progress } from "@/shared/components/ui/progress";
import { State } from "@/services/backend/deliveries/state/state";
import type { ReactNode } from "react";
import { CalendarDays, PackageCheck } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useGetDeliveryQuery } from "@/services/backend/deliveries/delivery/service";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { CustomerName } from "@/shared/components/domain/customer/customer-name";
import { formatDate } from "@/shared/utilities/date";
import { de } from "date-fns/locale";
import { UpdateDeliveryState } from "@/shared/components/domain/deliveries/update-delivery-state";
import { UpdateDeliveryCondition } from "@/shared/components/domain/deliveries/update-delivery-condition";

interface DeliveryStateProps {
  deliveryId: string;
}

export default function DeliveryState({ deliveryId }: DeliveryStateProps) {
  const icons: { [key: string]: ReactNode } = {
    [State.Announced]: <CalendarDays className="h-4 w-4" />,
    [State.Delivered]: <PackageCheck className="h-4 w-4" />,
  };

  const progress: { [key: string]: number } = {
    [State.Announced]: 50,
    [State.Delivered]: 100,
  };

  const { data: delivery, error } = useGetDeliveryQuery({
    id: deliveryId as string,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Card className="w-full shadow-none">
      <CardHeader className="flex items-start">
        <CardTitle className="text-3xl">Lieferstatus</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center gap-4">
          {delivery != null && (
            <div className="w-full space-y-4">
              <div className="grid w-full grid-cols-2 gap-4">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full",
                      delivery?.deliveryState === State.Delivered ||
                        delivery?.deliveryState === State.Announced
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {icons[State.Announced]}
                  </div>
                  <div className="mt-2 text-sm font-medium">Angekündigt</div>
                </div>
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full",
                      delivery?.deliveryState === State.Delivered
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {icons[State.Delivered]}
                  </div>
                  <div className="mt-2 text-sm font-medium">Angekommen</div>
                </div>
              </div>
              <Progress
                value={progress[delivery.deliveryState]}
                className={cn("w-full")}
              />
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-start space-y-4">
        {delivery != null && (
          <div>
            <div className="font-semibold">Kunde</div>
            <div>
              <CustomerName customerId={delivery.customerId} />
            </div>
          </div>
        )}
        {delivery != null &&
          delivery.deliveryState === State.Delivered &&
          delivery.deliveryDate !== "" && (
            <div>
              <div className="font-semibold">Ankunftsdatum</div>
              <div>{formatDate(delivery.deliveryDate, de)}</div>
            </div>
          )}
        {delivery != null && (
          <div className="flex flex-row space-x-2">
            <UpdateDeliveryState
              deliveryId={deliveryId}
              currentDeliveryState={delivery.deliveryState}
              disabled={delivery.immutable}
            />
            <UpdateDeliveryCondition
              deliveryId={deliveryId}
              currentCondition={delivery.condition}
              disabled={delivery.immutable}
            />
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
